/**
 * Render-time captions derived from an event's stored payload, shared between
 * the Episodes timeline and the "already scheduled" panels so the surfaces
 * can never drift. (Extraction precedent: episodeSourceRef →
 * constants/employmentEpisodes.js for ADR-138.)
 *
 * ADR-143 §12: captions must be WRITTEN, not inherited — the entry point
 * returns null for unrecognised codes, so every new event type ships blank
 * until a branch is added here. `employmentEventAnnotation` is the entry
 * point (renamed from `renewalAnnotation`, which named one branch, not the
 * job); `renewalAnnotation` and `amendmentAnnotation` are its branches.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { EMPLOYMENT_EVENT_TYPES } from './employmentEventTypes';
import { EVENT_VALIDATION_STATES } from './employmentValidationStates';

dayjs.extend(utc);

export const eventDayFmt = (d) => (d ? dayjs.utc(d).format('DD/MM/YYYY') : null);

/**
 * ADR-182 D1/D4 — the SWITCH DAY of a scheduled replacement: the morning the
 * renewal sweep enacts it. The cutover is the old contract's last day; the
 * sweep picks the renewal up once that day is over (ADR-179 D3b) and
 * radar-scheduler runs it every day, weekends included (ADR-182 D2), so the
 * switch day is always cutover + 1 as a calendar day (ADR-017). radar-api's
 * successor preview computes the same date (`switch.enactmentDate`).
 * @returns {string|null} YYYY-MM-DD
 */
export const renewalSwitchDay = (cutover) => (
  cutover ? dayjs.utc(cutover).add(1, 'day').format('YYYY-MM-DD') : null
);

export const renewalAnnotation = (event) => {
  const code = event.eventType?.code;
  const payload = event.payload;

  if (code === EMPLOYMENT_EVENT_TYPES.RENEWAL_SCHEDULED) {
    // Older rows (or a not-yet-regenerated client) may lack the payload —
    // fall back to the effective date, which IS the cutover.
    const cutover = eventDayFmt(payload?.cutoverDate) || eventDayFmt(event.effectiveOn);
    if (!cutover) return null;
    const start = eventDayFmt(payload?.contractStartDate);
    const end = eventDayFmt(payload?.contractEndDate);

    // ADR-182 D3 (S2) — "last day", never "ends … on": the cutover is the
    // last day IN FORCE, not the day something happens.
    const head = event.eventValidationState === EVENT_VALIDATION_STATES.CANCELLED
      ? `Was to end the current contract on ${cutover}`
      : `Last day of the current contract: ${cutover}`;
    const successor = start
      ? ` — successor starts ${start}${end ? `, fixed term until ${end}` : ', open-ended'}`
      : '';
    // Cancellable until the sweep runs — the switch day, not the cutover.
    const switchDay = eventDayFmt(renewalSwitchDay(payload?.cutoverDate || event.effectiveOn));
    const tail = event.eventValidationState === EVENT_VALIDATION_STATES.PENDING
      ? `. Cancellable until the switch on ${switchDay}.`
      : '';
    return `${head}${successor}${tail}`;
  }

  if (code === EMPLOYMENT_EVENT_TYPES.RENEWAL_CANCELLED) {
    const cutover = eventDayFmt(payload?.cancelledCutoverDate);
    const reason = payload?.reason;
    return `Scheduled replacement cancelled${cutover ? ` (was due ${cutover})` : ''}${reason ? ` — ${reason}` : ''}`;
  }

  return null;
};

// ADR-143 §4 — display names for amendable fields as they appear in an
// amendment's `changes`/`previous` payload keys.
const AMENDED_FIELD_LABELS = {
  // ADR-152 §1 — 'contract end date', matching the change-log field label and
  // the Contracts tab's own "Contract End Date" row. Amendment sentences and
  // change records interleave in one column (ADR-112 §11), so the two
  // vocabularies must agree or the same fact reads under two names.
  termEndDate: 'contract end date',
  contractCompanyId: 'contract company',
  contractTypeId: 'contract type',
  engagementModelId: 'engagement model',
  positionId: 'position',
  fullName: 'full name'
};

const amendedFieldPhrase = (field, previous, next, display) => {
  const label = AMENDED_FIELD_LABELS[field] || field;
  if (field === 'termEndDate') {
    const from = eventDayFmt(previous);
    const to = next === null ? 'open-ended' : eventDayFmt(next);
    return from ? `${label} ${from} → ${to}` : `${label} → ${to}`;
  }
  // Non-date fields carry ids; the payload's `display` map holds the
  // reference NAMES as they read at signing time (frozen server-side —
  // a later rename must not rewrite what the addendum said). Rows written
  // before display existed fall back to the field label alone.
  const names = display?.[field];
  if (names && (names.previous || names.next)) {
    return `${label} ${names.previous ?? '—'} → ${names.next ?? '—'}`;
  }
  return label;
};

/**
 * ADR-163 D5 (ADR-171 C6) — one position change is recorded on every contract
 * in force; each member says so (cancelling one cancels them all).
 */
export const amendmentGroupNote = (payload) =>
  (Number(payload?.groupSize) > 1 ? ` (one of ${payload.groupSize}, recorded on every contract in force)` : '');

/**
 * ADR-176 — what a revision moved, from the audit row's before/after pair.
 * Dates and names read as values; a reference id (company, type, engagement
 * model) reads as "new value", because the pair stores ids and the caption
 * makes no lookups — the revised amendment's own caption names it.
 */
export const amendmentRevisionPhrases = (before, after) => {
  if (!before || !after) return [];
  const phrases = [];
  if (before.effectiveOn !== after.effectiveOn) {
    phrases.push(`effective ${eventDayFmt(before.effectiveOn) ?? '—'} → ${eventDayFmt(after.effectiveOn) ?? '—'}`);
  }
  const changes = after.changes && typeof after.changes === 'object' ? after.changes : {};
  const previousChanges = before.changes && typeof before.changes === 'object' ? before.changes : {};
  Object.keys(changes).forEach((field) => {
    if (previousChanges[field] === changes[field]) {
      return;
    }
    const label = (AMENDED_FIELD_LABELS[field] || field).toLowerCase();
    if (field === 'fullName') {
      phrases.push(`${label} ${previousChanges[field] ?? '—'} → ${changes[field] ?? '—'}`);
    } else if (field === 'termEndDate') {
      phrases.push(`${label} ${eventDayFmt(previousChanges[field]) ?? '—'} → ${eventDayFmt(changes[field]) ?? '—'}`);
    } else {
      phrases.push(`new ${label}`);
    }
  });
  if (before.signedOn !== after.signedOn) {
    phrases.push(`signed on ${eventDayFmt(after.signedOn) ?? '—'}`);
  }
  if (before.reason !== after.reason) {
    phrases.push('reason reworded');
  }
  if (before.externalReference !== after.externalReference) {
    phrases.push(after.externalReference ? `Addendum reference ${after.externalReference}` : 'Addendum reference removed');
  }
  if (before.endReasonText !== after.endReasonText) {
    phrases.push('end reason reworded');
  }
  return phrases;
};

/**
 * ADR-143 §12 — captions for the amendment event types.
 * The §7 payload is self-describing (changes + previous), so the caption is
 * a pure read of the stored pair.
 */
export const amendmentAnnotation = (event) => {
  const code = event.eventType?.code;
  const payload = event.payload;

  if (code === EMPLOYMENT_EVENT_TYPES.CONTRACT_AMENDED) {
    const number = payload?.amendmentNumber;
    const head = `${number ? `Amendment #${number}` : 'Amendment'}${amendmentGroupNote(payload)}`;
    const changes = payload?.changes && typeof payload.changes === 'object' ? payload.changes : {};
    const previous = payload?.previous && typeof payload.previous === 'object' ? payload.previous : {};
    const display = payload?.display && typeof payload.display === 'object' ? payload.display : null;
    const phrases = Object.keys(changes)
      .map((field) => amendedFieldPhrase(field, previous[field], changes[field], display))
      .join(', ');
    const reason = payload?.reason ? ` — ${payload.reason}` : '';
    const ref = payload?.externalReference ? ` (${payload.externalReference})` : '';

    if (event.eventValidationState === EVENT_VALIDATION_STATES.CANCELLED) {
      return `${head}${ref} cancelled before taking effect${phrases ? `: ${phrases}` : ''}`;
    }
    if (event.eventValidationState === EVENT_VALIDATION_STATES.PENDING) {
      const effective = eventDayFmt(event.effectiveOn);
      return `${head}${ref}${phrases ? `: ${phrases}` : ''}${effective ? `, effective ${effective}` : ''}${reason}. Cancellable until then.`;
    }
    return `${head}${ref}${phrases ? `: ${phrases}` : ''}${reason}`;
  }

  if (code === EMPLOYMENT_EVENT_TYPES.AMENDMENT_REVISED) {
    const number = payload?.revisedAmendmentNumber;
    const moved = amendmentRevisionPhrases(payload?.before, payload?.after).join(', ');
    const reason = payload?.reason ? ` — ${payload.reason}` : '';
    return `Amendment${number ? ` #${number}` : ''} revised before taking effect${moved ? `: ${moved}` : ''}${reason}`;
  }

  if (code === EMPLOYMENT_EVENT_TYPES.AMENDMENT_CANCELLED) {
    const number = payload?.cancelledAmendmentNumber;
    const bySweep = payload?.cancelledBySweep;
    const reason = payload?.reason ? ` — ${payload.reason}` : '';
    return `Amendment${number ? ` #${number}` : ''} cancelled${bySweep ? ' by the system' : ''}${reason}`;
  }

  return null;
};

/**
 * The entry point both consumers import (§12): first branch that recognises
 * the code wins; unrecognised codes stay null — a caption is a deliberate
 * act, never inherited.
 */
export const employmentEventAnnotation = (event) =>
  renewalAnnotation(event) ?? amendmentAnnotation(event);

/* ──────────────────────────────────────────────────────────────────────
 * ADR-151 — the SAME amendment caption, split at the commas.
 *
 * `amendmentAnnotation` joins every changed field into one run-on clause:
 * "Amendment #1: contract type Contract of Mandate → Permanent, contract
 * company SOFTWARE MIND S.A. → SOFTWARE MIND OUTSOURCING SERVICES Sp. z
 * o.o. — Recorded from Enova synchronization". The separator between two
 * changes is a comma, and legal-entity names carry commas of their own, so
 * the reader has to parse the sentence to find out how many changes an
 * amendment made — which is the first thing they wanted to know.
 *
 * So the Episodes timeline asks for the caption in three pieces instead: the
 * head it already prints, the changes as ITEMS, and whatever the sentence
 * appended after them. A SEPARATE entry point, for the reason ADR-149 §7
 * gave: the prose function keeps its exact strings, so UpcomingEmployeeEvents,
 * the Cancel Amendment dialog and the change log cannot move because one
 * surface changed.
 *
 * Returns null for everything the timeline should still render as prose —
 * any non-amendment code, and deliberately a SINGLE-change amendment: one
 * bullet is a list with nothing to separate, and the sentence already reads.
 * ────────────────────────────────────────────────────────────────────── */

export const amendmentCaptionParts = (event) => {
  if (event?.eventType?.code !== EMPLOYMENT_EVENT_TYPES.CONTRACT_AMENDED) return null;
  const payload = event.payload;
  const changes = payload?.changes && typeof payload.changes === 'object' ? payload.changes : {};
  const fields = Object.keys(changes);
  if (fields.length < 2) return null;

  const previous = payload?.previous && typeof payload.previous === 'object' ? payload.previous : {};
  const display = payload?.display && typeof payload.display === 'object' ? payload.display : null;
  // The SAME helper the sentence uses — one amendment can never be described
  // two different ways on two surfaces (ADR-149 §7's rule, restated).
  const items = fields.map((field) => amendedFieldPhrase(field, previous[field], changes[field], display));

  const number = payload?.amendmentNumber;
  const head = `${number ? `Amendment #${number}` : 'Amendment'}${amendmentGroupNote(payload)}`;
  const ref = payload?.externalReference ? ` (${payload.externalReference})` : '';
  const reason = payload?.reason || null;

  if (event.eventValidationState === EVENT_VALIDATION_STATES.CANCELLED) {
    // The prose form appends no reason on this branch, and neither does this.
    return { lead: `${head}${ref} cancelled before taking effect:`, items, trail: null };
  }
  if (event.eventValidationState === EVENT_VALIDATION_STATES.PENDING) {
    // The sentence's tail reads ", effective 28/08/2026 — new. Cancellable
    // until then." A leading comma under a list has nothing to attach to, so
    // the same facts — no more, no fewer — become a following sentence.
    const effective = eventDayFmt(event.effectiveOn);
    const bits = [effective ? `Effective ${effective}` : null, reason].filter(Boolean);
    return {
      lead: `${head}${ref}:`,
      items,
      trail: `${bits.length ? `${bits.join(' — ')}. ` : ''}Cancellable until then.`
    };
  }
  return { lead: `${head}${ref}:`, items, trail: reason };
};

/* ─────────────────────────────────────────────────────────────────────────
 * ADR-149 §7 / the Contracts-tab timeline — the STRUCTURED sibling.
 *
 * `employmentEventAnnotation` above returns one prose SENTENCE per event,
 * which is what the Episodes timeline, UpcomingEmployeeEvents and the change
 * log render. The Contracts tab's "What happens next" list needs the same
 * facts as PARTS, so it can lay them out in columns: a date column, a kind
 * marker, the change itself, and labelled meta for what the sentence buries
 * — "(1231)" is an external reference and "— new" is a reason, and inside a
 * run-on neither reads as either.
 *
 * Deliberately a SEPARATE entry point rather than a refactor of the
 * annotation. Two reasons: the three existing consumers keep their exact
 * strings (no behaviour change outside the Contracts tab), and a branch
 * added here for a code the annotation does not caption —
 * TERMINATION_FINALIZED — cannot leak into them. ADR-143 §12's rule holds
 * for this entry point too: an unrecognised code returns null, never an
 * inherited default.
 *
 * `kind` is the row's semantic class; the CALLER maps it to colour, so the
 * palette stays with the component that paints. `date` is the raw value the
 * caller sorts on; `dateLabel` is that value formatted. `meta` is an array
 * of already-labelled fragments, empty when there is nothing to say.
 * ───────────────────────────────────────────────────────────────────────── */

export const EVENT_PART_KINDS = {
  AMENDMENT: 'AMENDMENT',
  CONTRACT_END: 'CONTRACT_END',
  SUCCESSOR: 'SUCCESSOR'
};

// `effectiveOn` reaches the client as either a date-only string or a full
// ISO timestamp depending on the route. Normalising to one day key here means
// callers can compare and sort it without knowing which they were handed.
export const eventDayKey = (d) => (d ? dayjs.utc(d).format('YYYY-MM-DD') : null);

const partsOf = ({ kind, label, headline, meta, date }) => ({
  kind,
  label,
  headline,
  meta: (meta || []).filter(Boolean),
  date: eventDayKey(date),
  dateLabel: eventDayFmt(date)
});

export const amendmentParts = (event) => {
  if (event?.eventType?.code !== EMPLOYMENT_EVENT_TYPES.CONTRACT_AMENDED) return null;
  const payload = event.payload;
  const number = payload?.amendmentNumber;
  const changes = payload?.changes && typeof payload.changes === 'object' ? payload.changes : {};
  const previous = payload?.previous && typeof payload.previous === 'object' ? payload.previous : {};
  const display = payload?.display && typeof payload.display === 'object' ? payload.display : null;

  // The phrase comes from the SAME helper the sentence uses, so the two
  // surfaces can never describe one amendment differently.
  const headline = Object.keys(changes)
    .map((field) => amendedFieldPhrase(field, previous[field], changes[field], display))
    .join(', ');

  return partsOf({
    kind: EVENT_PART_KINDS.AMENDMENT,
    label: number ? `Amendment #${number}` : 'Amendment',
    headline: headline || 'Amendment',
    meta: [
      payload?.externalReference ? `Ref ${payload.externalReference}` : null,
      payload?.reason ? `Reason: ${payload.reason}` : null
    ],
    date: event.effectiveOn
  });
};

export const renewalParts = (event) => {
  if (event?.eventType?.code !== EMPLOYMENT_EVENT_TYPES.RENEWAL_SCHEDULED) return null;
  const payload = event.payload;
  // The row is dated by when the SUCCESSOR starts, not by the cutover: the
  // cutover has its own row (this contract's scheduled end) wherever one
  // exists, and a successor listed on the day the old contract dies reads as
  // one event told twice. Rows written before the payload existed fall back
  // to the cutover, which IS `effectiveOn`.
  const start = payload?.contractStartDate || null;
  const cutover = payload?.cutoverDate || event.effectiveOn || null;
  const end = payload?.contractEndDate || null;
  if (!start && !cutover) return null;

  return partsOf({
    kind: EVENT_PART_KINDS.SUCCESSOR,
    label: 'Successor contract',
    headline: end
      ? `New contract starts, fixed term until ${eventDayFmt(end)}`
      : 'New contract starts, open-ended',
    // ADR-182 D3 (S1) — both dates stated. "That morning" bound to the cutover
    // in its own line, and was wrong for a gapped successor (row dated at its
    // later start, switch on cutover + 1).
    meta: [
      cutover ? `Last day of this contract: ${eventDayFmt(cutover)}` : null,
      cutover ? `Switch runs automatically on the morning of ${eventDayFmt(renewalSwitchDay(cutover))}` : null
    ],
    date: start || cutover
  });
};

export const contractEndParts = (event) => {
  if (event?.eventType?.code !== EMPLOYMENT_EVENT_TYPES.TERMINATION_FINALIZED) return null;
  const payload = event.payload;
  const reason = payload?.resignationReason || payload?.reason || null;
  // ADR-149 §8 — "employment closes" is true while a person holds one
  // contract, which is the invariant today. When ADR-144 phase 5 lands,
  // this line becomes "employment closes if this is their last open
  // contract", alongside the Resign / Cancel Contract copy split.
  return partsOf({
    kind: EVENT_PART_KINDS.CONTRACT_END,
    label: 'Contract ends',
    headline: 'The agreed term ends and employment closes on this date.',
    meta: [
      reason ? `Reason: ${reason}` : null,
      'Enacted automatically on the first weekday morning after the grace window'
    ],
    date: event.effectiveOn
  });
};

/**
 * The structured entry point. First branch that recognises the code wins;
 * unrecognised codes stay null — a row is a deliberate act, never inherited.
 */
export const employmentEventParts = (event) =>
  amendmentParts(event) ?? renewalParts(event) ?? contractEndParts(event);
